<script setup lang="ts" generic="t">
import TreeItem from './PlanroomTreeItem.vue';
import { watch, computed, reactive, ref } from 'vue';
import type { ComputedRef } from 'vue';
import TreeParent from './PlanroomTreeParent.vue';

const props = withDefaults(
  defineProps<{
    items: t[];
    itemKey: keyof t;
    childrenKey: keyof t;
    displayKey?: keyof t;
    collapseable?: boolean;
    selectable?: boolean;
    rootItem: t | null;
    depth?: number;
    defaultExpandedState?: boolean;
  }>(),
  {
    rootItem: null,
    collapseable: true,
    selectable: false,
    defaultExpandedState: false,
    depth: 0,
  },
);

const selectedItems = defineModel<t[]>('selectedItems', { default: [] });

const emit = defineEmits<{
  'update:selectedItems': [t[]];
  'update:parentSelections': [Map<t, boolean>];
  childSelected: [t, boolean];
}>();

// const selectedItemsSet = reactive(new Set());
const getInitialSelectedParents = () => {
  const initialSelectedParents = new Map();

  updateSelectedParents(initialSelectedParents);

  return initialSelectedParents;
};

const updateSelectedParents = (parentSelections: Map) => {
  const parentItems = props.items.filter(
    (item) => (item[props.childrenKey]?.length ?? 0) > 0,
  );

  parentItems.forEach((parent) => {
    const childItems = parent[props.childrenKey];
    const selectedChildren = childItems.filter((child) =>
      selectedItems.value.includes(child),
    );
    if (selectedChildren.length === 0)
      return parentSelections.set(parent, false);
    if (selectedChildren.length === childItems.length)
      return parentSelections.set(parent, true);
    return parentSelections.set(parent, null);
  });
};

const parentSelections = reactive(getInitialSelectedParents());
const parents = ref();

const setParentsSelected = (val: boolean) => {
  props.items
    .filter((item) => {
      return (item[props.childrenKey]?.length ?? 0) !== 0;
    })
    .map((item) =>
      parents.value.find((parent: typeof TreeParent) => parent.item === item),
    )
    .forEach((parent) => {
      parent.setChildrenSelected(val);
    });
};

const resetSelection = () => {
  selectedItems.value.length = 0;
  parents.value?.forEach((parent: typeof TreeParent) => {
    parent.resetChildSelections();
  });
};

const setAllItemsSelected = (value: boolean) => {
  props.items.forEach((item) => {
    if ((item[props.childrenKey]?.length ?? 0) === 0) {
      setItemSelected(item, value);
    } else {
      setParentSelected(item, value);
    }
  });
};

const setItemSelected = (item: t, value: boolean) => {
  if (value) {
    if (!selectedItems.value.includes(item)) {
      selectedItems.value.push(item);
      emit('childSelected', item, value);
    }
  } else {
    const idx = selectedItems.value.findIndex(
      (selectedItem) => item === selectedItem,
    );
    if (idx !== -1) {
      selectedItems.value.splice(idx, 1);
      emit('childSelected', item, value);
    }
  }
};

const setParentSelected = (item: t, value: boolean) => {
  parentSelections.set(item, value);
};

const setAllExpanded = (val: boolean) => {
  parents.value?.forEach((parent: typeof TreeParent) => {
    parent.setAllExpanded(val);
  });
};

const isItemSelected = (item: t) => {
  let selected = false;
  for (const i of selectedItems.value) {
    if (parseInt(i.id) === parseInt(item.id)) {
      selected = true;
      break;
    }
  }
  return selected;
};

const hasParents = computed(() => {
  return parents.value?.length > 0;
});

watch(parentSelections, () => {
  emit('update:parentSelections', parentSelections);
});

watch(
  selectedItems,
  () => {
    updateSelectedParents(parentSelections);
  },
  { deep: true },
);

defineExpose({
  resetSelection,
  setAllItemsSelected,
  setParentsSelected,
  setAllExpanded,
  hasParents,
});

const isDraggedOver = ref(false);
const dragOver = () => {
  isDraggedOver.value = true;
};
const dragLeave = () => {
  isDraggedOver.value = false;
};
const drop = (e: DragEvent) => {
  isDraggedOver.value = false;
};
const handleDroppedItem = (child: t) => {
  console.log('child item has moved', {
    item: child,
    oldParent: props.rootItem,
  });
};
</script>
<template>
  <ul
    :class="{
      'planroom-tree': true,
      'planroom-tree-root': depth === 0,
      'ps-ui-element': true,
      dragOver: isDraggedOver,
    }"
    @dragover.prevent.stop="dragOver"
    @dragleave="dragLeave"
    @drop.stop="drop"
  >
    <li v-for="item in items" :key="item[itemKey]">
      <TreeParent
        v-if="(item[childrenKey]?.length ?? 0) > 0"
        ref="parents"
        :item="item"
        :parent="rootItem"
        :item-key="itemKey"
        :children-key="childrenKey"
        :selected="parentSelections.get(item)"
        :expanded="defaultExpandedState"
        :default-expanded-state="defaultExpandedState"
        :depth="depth"
        :selected-items="selectedItems"
        :selectable="selectable"
        @update:selected="(val: boolean) => setParentSelected(item, val)"
      >
        <template #item="childItem">
          <slot name="item" v-bind="childItem"></slot>
        </template>
      </TreeParent>
      <TreeItem
        v-else
        :item="item"
        :collapseable="false"
        :parent="rootItem"
        :selected="isItemSelected(item)"
        :selectable="selectable"
        :expanded="defaultExpandedState"
        :depth="depth"
        @update:selected="(val: boolean) => setItemSelected(item, val)"
        @item-dropped="handleDroppedItem"
      >
        <template #item="childItem">
          <slot name="item" v-bind="childItem"></slot>
        </template>
      </TreeItem>
    </li>
  </ul>
</template>
<style>
.planroom-tree {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0;
  margin: 0;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;

  > li {
    width: 100%;
  }

}
</style>
